import { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Hash, Lock, Users, X, Shield, AlertTriangle } from 'lucide-react';
import { channelsApi, AUTOMOD_ACTION_LABELS, AutomodAction } from '@/api/channels';
import { useAuthStore } from '@/store/authStore';
import { useWorkspaceStore } from '@/store/workspaceStore';

interface Props {
  channelId: string;
  onClose: () => void;
}

export default function ChannelProfileModal({ channelId, onClose }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // Phase 13 — workspace-aware: видимость isDefault checkbox требует знать
  // принадлежит ли канал текущему workspace и является ли user его владельцем.
  const workspace = useWorkspaceStore((s) => s.current);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [description, setDescription] = useState('');
  const [topic, setTopic] = useState('');
  const [mediaEnabled, setMediaEnabled] = useState(true);
  const [isDefaultLocal, setIsDefaultLocal] = useState(false);
  const [chatStyle, setChatStyle] = useState<'BUBBLE' | 'CLASSIC'>('BUBBLE');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['channel-profile', channelId],
    queryFn: () => channelsApi.getProfile(channelId),
  });

  useEffect(() => {
    if (profile) {
      setDescription(profile.description ?? '');
      setTopic(profile.topic ?? '');
      setMediaEnabled(profile.mediaEnabled !== false);
      setIsDefaultLocal(profile.isDefault === true);
      setChatStyle((profile as any).chatStyle === 'CLASSIC' ? 'CLASSIC' : 'BUBBLE');
    }
  }, [profile]);

  const uploadAvatarMut = useMutation({
    mutationFn: (file: File) => channelsApi.uploadAvatar(channelId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-profile', channelId] });
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
    },
  });

  const updateMut = useMutation({
    mutationFn: (data: { description?: string; topic?: string; mediaEnabled?: boolean; isDefault?: boolean; chatStyle?: 'BUBBLE' | 'CLASSIC' }) =>
      channelsApi.update(channelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-profile', channelId] });
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
      setEditMode(false);
    },
  });

  const handleAvatarClick = () => {
    if (canEdit) fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadAvatarMut.mutate(file);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md p-8 text-center">
          <p className="text-surface-500 text-sm">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const isPrivateForGuest = profile.type === 'PRIVATE' && !profile.isMember;
  const canEdit = profile.isMember && (profile.memberRole === 'OWNER' || profile.memberRole === 'ADMIN');
  // Phase 13 — checkbox isDefault показываем только владельцу workspace
  // (или global SUPER_ADMIN — менее частый кейс) и только если канал
  // принадлежит ТОМУ ЖЕ workspace, в котором мы сейчас сидим.
  const canEditIsDefault =
    profile.workspaceId != null &&
    workspace?.id === profile.workspaceId &&
    workspace?.ownerId != null &&
    user?.id === workspace.ownerId;
  const apiBase = import.meta.env.VITE_API_URL ?? (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header banner */}
        <div className="h-24 bg-gradient-to-r from-brand-700 to-brand-900 relative">
          <button onClick={onClose} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Avatar */}
        <div className="px-5 -mt-10 relative">
          <div
            onClick={handleAvatarClick}
            className={`w-20 h-20 rounded-full border-4 border-surface-900 bg-surface-800 flex items-center justify-center overflow-hidden ${canEdit ? 'cursor-pointer group' : ''}`}
          >
            {profile.avatarUrl ? (
              <img src={`${apiBase}${profile.avatarUrl}`} alt={profile.displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-brand-700 flex items-center justify-center">
                {profile.type === 'PRIVATE' ? <Lock size={28} className="text-white" /> : <Hash size={28} className="text-white" />}
              </div>
            )}
            {canEdit && (
              <div className="absolute inset-0 w-20 h-20 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera size={20} className="text-white" />
              </div>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <h2 className="text-lg font-bold text-surface-100">{profile.displayName}</h2>
            <p className="text-surface-500 text-xs font-mono">#{profile.name}</p>
          </div>

          {/* Private channel guard */}
          {isPrivateForGuest ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-surface-800 rounded-lg px-3 py-2">
                <Lock size={14} className="text-yellow-400" />
                <span className="text-yellow-400 text-sm font-medium">Закрытый приватный канал</span>
              </div>
              {profile.description && (
                <div>
                  <p className="text-surface-500 text-xs font-medium mb-1">Описание</p>
                  <p className="text-surface-300 text-sm">{profile.description}</p>
                </div>
              )}
              <div className="flex items-center gap-1 text-surface-500 text-xs">
                <Users size={12} />
                <span>{profile._count?.members} участников</span>
              </div>
            </div>
          ) : (
            <>
              {/* Member info */}
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1 text-surface-400">
                  <Users size={14} />
                  <span>{profile._count?.members} участников</span>
                </div>
                {profile.type === 'PRIVATE' && (
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Lock size={12} />
                    <span>Приватный</span>
                  </div>
                )}
              </div>

              {/* Edit mode */}
              {editMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-surface-500 text-xs font-medium mb-1 block">Тема</label>
                    <input
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      maxLength={256}
                      className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 outline-none focus:border-brand-500"
                      placeholder="Тема канала..."
                    />
                  </div>
                  <div>
                    <label className="text-surface-500 text-xs font-medium mb-1 block">Описание</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={1024}
                      rows={3}
                      className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 outline-none focus:border-brand-500 resize-none"
                      placeholder="Описание канала..."
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mediaEnabled}
                      onChange={(e) => setMediaEnabled(e.target.checked)}
                      className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-surface-300 text-sm">Разрешить загрузку фото</span>
                  </label>
                  <div>
                    <p className="text-surface-500 text-xs font-medium mb-1.5">Стиль чата</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setChatStyle('BUBBLE')}
                        className={`flex-1 px-3 py-1.5 rounded-lg text-sm border ${chatStyle === 'BUBBLE' ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface-800 text-surface-300 border-surface-700'}`}
                      >Пузырьки</button>
                      <button
                        type="button"
                        onClick={() => setChatStyle('CLASSIC')}
                        className={`flex-1 px-3 py-1.5 rounded-lg text-sm border ${chatStyle === 'CLASSIC' ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface-800 text-surface-300 border-surface-700'}`}
                      >IRC Классик</button>
                    </div>
                  </div>
                  {canEditIsDefault && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isDefaultLocal}
                        onChange={(e) => setIsDefaultLocal(e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500 flex-shrink-0"
                      />
                      <span className="text-surface-300 text-sm">
                        Канал по умолчанию для новых участников workspace
                        <span className="block text-surface-500 text-xs mt-0.5">
                          Зарегистрировавшиеся через {workspace?.slug}.* юзеры будут авто-добавлены сюда.
                        </span>
                      </span>
                    </label>
                  )}
                  {/* v5.2 — Moderation pack section. Admin-only. Read on mount,
                      sync через @tanstack/react-query separately. */}
                  <ModerationSection channelId={channelId} />
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateMut.mutate({
                        description: description || undefined,
                        topic: topic || undefined,
                        mediaEnabled,
                        chatStyle,
                        ...(canEditIsDefault ? { isDefault: isDefaultLocal } : {}),
                      })}
                      disabled={updateMut.isPending}
                      className="btn-primary flex-1"
                    >
                      {updateMut.isPending ? 'Сохранение...' : 'Сохранить'}
                    </button>
                    <button onClick={() => setEditMode(false)} className="btn-ghost flex-1">
                      Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Topic */}
                  {profile.topic && (
                    <div>
                      <p className="text-surface-500 text-xs font-medium mb-1">Тема</p>
                      <p className="text-surface-300 text-sm">{profile.topic}</p>
                    </div>
                  )}

                  {/* Description */}
                  {profile.description && (
                    <div>
                      <p className="text-surface-500 text-xs font-medium mb-1">Описание</p>
                      <p className="text-surface-300 text-sm">{profile.description}</p>
                    </div>
                  )}

                  {!profile.topic && !profile.description && (
                    <p className="text-surface-600 text-sm italic">Нет описания</p>
                  )}

                  {/* Edit button */}
                  {canEdit && (
                    <button
                      onClick={() => {
                        setDescription(profile.description ?? '');
                        setTopic(profile.topic ?? '');
                        setEditMode(true);
                      }}
                      className="btn-ghost w-full"
                    >
                      Редактировать профиль
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// v5.2 — Moderation pack inline editor.
// Renders inside ChannelProfileModal edit-mode. Mounts only when admin opens
// "Edit", does its own GET /:id/moderation + PATCH on save.
// ────────────────────────────────────────────────────────────────────────────
function ModerationSection({ channelId }: { channelId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['channel-moderation', channelId],
    queryFn: () => channelsApi.getModeration(channelId),
  });
  const patchMut = useMutation({
    mutationFn: (patch: Partial<{
      isAdult: boolean;
      slowModeSeconds: number;
      automodBannedWords: string[];
      automodPatterns: string[];
      automodAction: AutomodAction;
      automodTimeoutSec: number;
    }>) => channelsApi.patchModeration(channelId, patch),
    onSuccess: (next) => {
      qc.setQueryData(['channel-moderation', channelId], next);
      qc.invalidateQueries({ queryKey: ['channel-profile', channelId] });
    },
  });

  // Local edit state — синхронизируется с server data при load.
  const [bannedText, setBannedText] = useState('');
  const [patternsText, setPatternsText] = useState('');
  useEffect(() => {
    if (data) {
      setBannedText(data.automodBannedWords.join('\n'));
      setPatternsText(data.automodPatterns.join('\n'));
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <div className="border-t border-surface-800 pt-3 mt-2">
        <p className="text-surface-500 text-xs italic">Загрузка модерации...</p>
      </div>
    );
  }

  const slowOptions: { v: number; label: string }[] = [
    { v: 0, label: 'Выкл' },
    { v: 5, label: '5 сек' },
    { v: 10, label: '10 сек' },
    { v: 30, label: '30 сек' },
    { v: 60, label: '1 мин' },
    { v: 300, label: '5 мин' },
    { v: 900, label: '15 мин' },
  ];

  const busy = patchMut.isPending;

  return (
    <div className="border-t border-surface-800 pt-3 mt-2 space-y-3">
      <div className="flex items-center gap-2">
        <Shield size={14} className="text-brand-400" />
        <span className="text-surface-200 text-sm font-semibold">Модерация (v5.2)</span>
      </div>

      {/* 18+ flag */}
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={data.isAdult}
          disabled={busy}
          onChange={(e) => patchMut.mutate({ isAdult: e.target.checked })}
          className="w-4 h-4 mt-0.5 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500 flex-shrink-0"
        />
        <span className="text-surface-300 text-sm">
          Канал 18+
          <span className="block text-surface-500 text-xs mt-0.5">
            Юзеру нужно включить «adult content» в настройках и иметь достаточную репутацию.
          </span>
        </span>
      </label>

      {/* Slow-mode */}
      <div>
        <label className="text-surface-500 text-xs font-medium mb-1 block">
          Медленный режим
        </label>
        <select
          value={data.slowModeSeconds}
          disabled={busy}
          onChange={(e) => patchMut.mutate({ slowModeSeconds: Number(e.target.value) })}
          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 outline-none focus:border-brand-500"
        >
          {slowOptions.map((o) => (
            <option key={o.v} value={o.v}>{o.label}</option>
          ))}
        </select>
        <p className="text-surface-500 text-xs mt-1">
          Между сообщениями обычных участников. Owner / Admin / Moderator не ограничены.
        </p>
      </div>

      {/* Banned words */}
      <div>
        <label className="text-surface-500 text-xs font-medium mb-1 block">
          Запрещённые слова
        </label>
        <textarea
          value={bannedText}
          rows={3}
          disabled={busy}
          onChange={(e) => setBannedText(e.target.value)}
          onBlur={() => {
            const list = bannedText.split('\n').map((w) => w.trim()).filter(Boolean);
            patchMut.mutate({ automodBannedWords: list });
          }}
          placeholder="по слову на строку"
          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 outline-none focus:border-brand-500 resize-none font-mono"
        />
        <p className="text-surface-500 text-xs mt-1">Регистр игнорируется, поиск целым словом.</p>
      </div>

      {/* Regex patterns */}
      <div>
        <label className="text-surface-500 text-xs font-medium mb-1 block flex items-center gap-1">
          Regex-паттерны
          <span className="text-surface-600">(advanced)</span>
        </label>
        <textarea
          value={patternsText}
          rows={3}
          disabled={busy}
          onChange={(e) => setPatternsText(e.target.value)}
          onBlur={() => {
            const list = patternsText.split('\n').map((w) => w.trim()).filter(Boolean);
            patchMut.mutate({ automodPatterns: list });
          }}
          placeholder="по regex на строку (например: t\\.me/\\w+)"
          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 outline-none focus:border-brand-500 resize-none font-mono"
        />
      </div>

      {/* Action + timeout */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-surface-500 text-xs font-medium mb-1 block">Действие</label>
          <select
            value={data.automodAction}
            disabled={busy}
            onChange={(e) => patchMut.mutate({ automodAction: e.target.value as AutomodAction })}
            className="w-full bg-surface-800 border border-surface-700 rounded-lg px-2 py-2 text-sm text-surface-200 outline-none"
          >
            {(Object.keys(AUTOMOD_ACTION_LABELS) as AutomodAction[]).map((a) => (
              <option key={a} value={a}>{AUTOMOD_ACTION_LABELS[a]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-surface-500 text-xs font-medium mb-1 block">Mute (сек)</label>
          <input
            type="number"
            min={10}
            max={86400}
            value={data.automodTimeoutSec}
            disabled={busy || data.automodAction !== 'TIMEOUT'}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n >= 10 && n <= 86400) {
                patchMut.mutate({ automodTimeoutSec: n });
              }
            }}
            className="w-full bg-surface-800 border border-surface-700 rounded-lg px-2 py-2 text-sm text-surface-200 outline-none disabled:opacity-50"
          />
        </div>
      </div>

      {patchMut.isError && (
        <div className="flex items-start gap-2 bg-red-900/20 border border-red-900 rounded-lg px-3 py-2">
          <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
          <span className="text-red-300 text-xs">
            {(patchMut.error as Error)?.message ?? 'Ошибка сохранения'}
          </span>
        </div>
      )}
    </div>
  );
}
